

import React, { useState, useMemo, useEffect } from "react";
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  MenuItem,
  Typography,
  Paper,
  Divider,
  Select,
  InputLabel,
  FormControl,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import "./FieldEditor.scss";
import IconButtons from "../../../components/IconButtons/IconButtons";
import { active_plus_icon, delete_active_icon } from "../../../constants/icons";
import { font_13 } from "../../../utils/utils";
import AutoCompleteTagInput from "../../../components/AutoCompleteTagInput/AutoCompleteTagInput";

const OPTION_TYPES = [
  "RADIO_OPTIONS",
  "SELECT",
  "CHECKBOX_OPTIONS",
  "DROPDOWN_MULTI_SELECT",
  "DROPDOWN_SINGLE_SELECT",
];

const DEFAULT_OPTIONS = ["Option 1"];

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 250,
    },
  },
};

const OPTION_REGEX = /^(?!\s)(?!.*\|).+$/;


const getRangeInputState = (value, isTouched, isRangeError, label, parentErrors) => {
  if (parentErrors?.range) {
    return {
      error: true,
      text: parentErrors.range
    };
  }
  const isEmpty = value === "" || value === null || value === undefined;
  if (isTouched && isEmpty) {
    return { error: true, text: `${label} is required` };
  }
  if (isRangeError && !isEmpty) {
    return {
      error: true,
      text: label === "Min" ? "Min must be < Max" : "Max must be > Min"
    };
  }
  return { error: false, text: "" };
};

const FieldEditor = ({ field, validationErrors, fields = [], onChange, handleError = () => { } }) => {
  const [rangeError, setRangeError] = useState(false);
  const [isDuplicateLabel, setDuplicateLabel] = useState(false);
  const [isLabelMissing, setLabelMissing] = useState(false);
  const [selectedOption, setSelectedOption] = useState(0);
  const [minTouched, setMinTouched] = useState(false);
  const [maxTouched, setMaxTouched] = useState(false);

  const formInputType = useSelector(
    (state) => state?.utility?.constants?.formInputType || []
  );
  const DEPENDENT_CONDITIONS = useSelector(
    (state) => state?.utility?.constants?.dependentCondition
  );

  const hasDependency = field.dependentOnIds && field.dependentOnIds.length > 0;
  const isAvgScore = field.type?.toUpperCase() === "AVERAGE_SCORE";
  const isScaleEnabled = Boolean(field?.__meta?.scaleCheckBoxEnabled);

  const optionsForSelect = useMemo(() => {
    return (field?.options || [])
      .filter(o => typeof o === "string" ? o.trim() !== "" : (o?.label || "").trim() !== "")
      .map((o) => {
        const val = typeof o === "string" ? o : o.label;
        return { label: val, value: typeof o === "string" ? o : (o.value ?? val) };
      });
  }, [field?.options]);


  if (!field) return null;
  const isAverageScore = field.type === "AVERAGE_SCORE";
  const setField = (partial) => {
    let updatedField = { ...field, ...partial };
    if (partial.dependentOnIds && partial.dependentOnIds.length > 0) {
      updatedField.required = false;
    }
    else if (partial.dependentOnIds && partial.dependentOnIds.length === 0) {
    }
    onChange({ ...field, ...partial });
  };



  const addOption = () => {
    const currentOptions = Array.isArray(field.options) ? [...field.options] : [];
    const updated = [...currentOptions, ""];
    const currentErrors = Array.isArray(field.optionErrors) ? [...field.optionErrors] : [];
    const updatedErrors = [...currentErrors, "Option cannot be empty"];
    handleError(true);

    setField({
      options: updated,
      optionErrors: updatedErrors
    });
  };



  const validateSingleOption = (option, allOptions) => {
    const currentVal = option || "";
    const trimmedVal = currentVal.trim();
    if (!trimmedVal) return "Option value is required";
    if (!OPTION_REGEX.test(currentVal)) {
      if (currentVal.startsWith(" ")) return "Option cannot start with a space";
      if (currentVal.includes("|")) return "Option cannot contain the '|' character";
      return "Invalid option value";
    }
    const duplicateCount = allOptions.filter(o => (o || "").trim() === trimmedVal).length;
    if (duplicateCount > 1) return "Duplicate option value";
    return false;
  };


  const updateOption = (index, value) => {
    const currentOptions = Array.isArray(field.options) ? [...field.options] : [];
    currentOptions[index] = value ?? "";
    const optionErrors = currentOptions.map(opt => validateSingleOption(opt, currentOptions));
    const hasAnyError = optionErrors.some(error => error !== false);
    handleError(hasAnyError);
    setField({
      options: currentOptions,
      optionErrors
    });
  };


  const deleteOption = (index) => {
    const updatedOptions = (field.options || []).filter((_, i) => i !== index);
    const updatedErrors = (field.optionErrors || []).filter((_, i) => i !== index);
    const hasRemainingOptionErrors = updatedErrors.some(error => error !== false);
    const isCurrentlyInvalid = hasRemainingOptionErrors || isLabelMissing || isDuplicateLabel;
    handleError(isCurrentlyInvalid);
    onChange({
      ...field,
      options: updatedOptions,
      optionErrors: updatedErrors
    });
  };



  const handleTypeChange = (e) => {
    const newType = e.target.value;

    setField({
      ...field,
      type: newType,
      label: "",
      description: "",
      required: true,
      defaultValue: "",
      options: [],
      minValue: "1",
      maxValue: "5",


      dependentOnIds: [],
      dependentValue: "",
      dependentCondition: null,
    });
  };



  const checkDuplicateLabel = (label) => {
    const trimmed = (label || "").trim();
    const isDuplicate = fields.some((item) => {
      if (!item || !item.label) return false;
      const currentKey = field.id ?? field.tempId;
      const itemKey = item.id ?? item.tempId;
      if (currentKey && itemKey === currentKey) return false;
      return (item.label || "").toLowerCase() === trimmed.toLowerCase();
    });
    handleError(isDuplicate);
    return isDuplicate;
  };

  const handleBlur = () => {
    const trimmed = (field.label || "").trim();
    const isRequiredMissing = trimmed === "";
    const isDuplicate = checkDuplicateLabel(trimmed);
    setDuplicateLabel(isDuplicate);
    setLabelMissing(isRequiredMissing);
    const hasOptionErrors = (field.optionErrors || []).some(err => err !== false);
    handleError(isRequiredMissing || isDuplicate || hasOptionErrors);
    onChange({ ...field, label: trimmed });
  };

  const handleCheckBox = (e) => {
    const checked = e.target.checked;

    setField({
      __meta: {
        ...field.__meta,
        scaleCheckBoxEnabled: checked,
      },
      defaultValue: checked ? null : field.defaultValue,
      options: checked ? ["scaleCheckBoxEnabled"] : [],
    });
  };


  const handleOptionChange = (event) => {
    const newOption = +event.target.value;
    setSelectedOption(newOption, null);
  };

  const renderOptions = () => {
    if (!field.options || field.options.length === 0) return null;
    const isBackendGenerated = ["PERFORMANCE_ASSESSED_BY", "PROJECT_NAME"].includes(field.type);
    return (field.options || []).map((opt, idx) => {
      const displayValue = typeof opt === "string" ? opt : (opt.label ?? "");
      return (
        <Box
          key={idx}
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            background: "#fff",
            mb: 1,
          }}
        >
          <TextField
            fullWidth
            label={`Option ${idx + 1}`}
            disabled={isBackendGenerated}
            margin="dense"
            size="small"
            color="secondary"
            variant="outlined"
            className="text-input"
            value={displayValue}
            onChange={(e) => updateOption(idx, e.target.value)}
            error={!!field.optionErrors?.[idx]}
            helperText={field.optionErrors?.[idx] || ""}
          />

          {!isBackendGenerated && (
            <IconButtons
              size="small"
              color="error"
              image={delete_active_icon}
              handleClick={() => deleteOption(idx)}
              width={font_13}
              height={font_13}
              tooltip="Delete Option"
            />
          )}
        </Box>
      );
    });
  };

  const handleDefaultChange = (value) => {
    const isNumericType = ["NUMERIC_TEXT", "AVERAGE_SCORE"].includes(field.type);
    if (isNumericType && value !== "" && (Number(value) < 0 || Number(value) > 10)) {
      return;
    }
    setField({ defaultValue: value });
  };

  const handleMinChange = (val) => {
    if (val !== "" && (Number(val) < 0 || Number(val) > 10)) return;
    const min = val === "" ? "" : Number(val);
    setField({ minValue: min });
    if (min !== "" && field.maxValue !== "" && Number(min) < Number(field.maxValue)) {
      setRangeError(false);
    }
  };

  const handleMaxChange = (val) => {
    if (val !== "" && (Number(val) < 0 || Number(val) > 10)) return;
    const max = val === "" ? "" : Number(val);
    setField({ maxValue: max });
    if (field.minValue !== "" && max !== "" && Number(max) > Number(field.minValue)) {
      setRangeError(false);
    }
  };

  const NUMERIC_FIELD_TYPES = ["NUMERIC_TEXT", "AVERAGE_SCORE"];

  const getDependencyFieldOptions = () => {
    return fields
      .filter((f) => {
        const currentId = field.id || field.tempId;
        const targetId = f.id || f.tempId;
        const isNotSelf = currentId !== targetId;
        const isNumeric = f.type && NUMERIC_FIELD_TYPES.includes(f.type.toUpperCase());
        return isNotSelf && isNumeric;
      })
      .map((f) => ({
        label: f.label,
        value: f.tempId || f.id,
      }));
  };

  const handleSelectDependencyField = (id) => {
    const ids = id ? (Array.isArray(id) ? id : [id]) : [];
    const isClearing = ids.length === 0 || ids[0] === "" || ids[0] === undefined;
    const updatedPartial = {
      dependentOnIds: isClearing ? [] : ids,
      dependentValue: isClearing ? "" : field.dependentValue,
      dependentCondition: isClearing ? null : field.dependentCondition,
      required: isClearing ? field.required : false
    };

    if (isClearing) {
      const hasOptionErrors = (field.optionErrors || []).some(err => err !== false);
      handleError(isLabelMissing || isDuplicateLabel || hasOptionErrors);
    }

    setField(updatedPartial);
  };

  const handleSelectDependencyValue = (val) => {
    if (val === "") {
      setField({ dependentValue: "" });
      return;
    }
    const numberValue = Number(val);
    if (!Number.isNaN(numberValue)) {
      setField({
        dependentValue: numberValue,
      });
    }
  };


  const handleSelectDependencyCondition = (condition) => {
    setField({
      dependentCondition: condition ?? null,
    });
  };

  const getHelperText = () => {
    if (isLabelMissing) return "Label is required!";
    if (isDuplicateLabel) return "Duplicate Label!";
    return "";
  };

  const renderTypeSpecificControls = () => {
    switch (field.type) {
      case "TEXTAREA":
        return (
          <TextField
            label="Default Text"
            className="text-input"
            fullWidth
            size="small"
            color="secondary"
            value={field.defaultValue || ""}
            onChange={(e) => handleDefaultChange(e.target.value)}
            multiline
            rows={4}
            sx={{ mb: 1.5 }}
          />
        );

      case "NUMERIC_TEXT":
        const minState = getRangeInputState(field.minValue, minTouched, rangeError, "Min", validationErrors);
        const maxState = getRangeInputState(field.maxValue, maxTouched, rangeError, "Max", validationErrors);
        return (
          <>
            <TextField
              label="Default Value"
              type="number"
              fullWidth
              size="small"
              color="secondary"
              disabled={isScaleEnabled}
              value={field.defaultValue ?? ""}
              onChange={(e) => handleDefaultChange(e.target.value === "" ? "" : Number(e.target.value))}
              error={Boolean(validationErrors?.defaultValue)}
              helperText={validationErrors?.defaultValue}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <TextField
                label="Min"
                type="number"
                fullWidth
                size="small"
                color="secondary"
                required
                value={field.minValue ?? ""}
                onChange={(e) => handleMinChange(e.target.value)}
                onBlur={() => setMinTouched(true)}
                error={minState.error}
                helperText={minState.text}
              />
              <TextField
                label="Max"
                type="number"
                fullWidth
                size="small"
                color="secondary"
                required
                value={field.maxValue ?? ""}
                onChange={(e) => handleMaxChange(e.target.value)}
                onBlur={() => setMaxTouched(true)}
                error={maxState.error}
                helperText={maxState.text}
              />
            </Box>

            <Box>
              <div className="scale-checkbox-container">
                <FormControlLabel
                  sx={{ mt: 0, ml: 0 }}

                  control={
                    <Checkbox
                      className="scale-checkbox"
                      size="small"
                      color="secondary"
                      checked={isScaleEnabled}
                      onChange={handleCheckBox}
                    />

                  }
                  label={<Typography sx={{ fontSize: "0.85rem", }}>Show Scale</Typography>}
                />
              </div>

            </Box>
          </>
        );

      case "RADIO_OPTIONS":
        return (
          <>
            <Typography sx={{ fontSize: "0.85rem", mt: 1, mb: 0.7 }}>
              Radio Options
            </Typography>
            <FormControl fullWidth size="small" variant="outlined" className="select-input">
              <InputLabel id="default-radio-label">Default</InputLabel>
              <Select
                labelId="default-radio-label"
                size="small"
                className="text-input select-input"
                color="secondary"
                value={field.defaultValue || null}
                onChange={(e) => handleDefaultChange(e.target.value)}
                label="Default"
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {optionsForSelect.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </>
        );

      case "CHECKBOX_OPTIONS":
        return (
          <>
            <Typography sx={{ fontSize: "0.85rem", mt: 1, mb: 0.7 }}>
              Checkbox Options — default selection
            </Typography>
            <FormControl fullWidth size="small" variant="outlined" className="select-input">
              <InputLabel id="default-checkbox-label">Default</InputLabel>
              <Select
                labelId="default-checkbox-label"
                size="small"
                className="text-input select-input"
                color="secondary"
                value={field.defaultValue || null}
                onChange={(e) => handleDefaultChange(e.target.value)}
                label="Default"
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {optionsForSelect.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        );

      case "DROPDOWN_SINGLE_SELECT":
        return (
          <FormControl fullWidth size="small" sx={{ mb: 1, mt: 1 }} variant="outlined" className="select-input">
            <InputLabel id="dropdown-single-label">Default</InputLabel>
            <Select
              labelId="dropdown-single-label"
              value={field.defaultValue ?? ""}
              label="Default"
              size="small"
              className="text-input select-input"
              color="secondary"
              onChange={(e) => handleDefaultChange(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {optionsForSelect.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "DROPDOWN_MULTI_SELECT":
      case "DROPDOWN_MULTI":
        return (
          <FormControl
            fullWidth
            size="small"
            sx={{ mb: 1, mt: 1 }}
            variant="outlined"
            className="select-input"
          >
            <InputLabel id="dropdown-single-label">Default</InputLabel>

            <Select
              labelId="dropdown-single-label"
              value={field.defaultValue ?? ""}
              label="Default"
              size="small"
              className="text-input select-input"
              color="secondary"
              onChange={(e) => handleDefaultChange(e.target.value)}
              MenuProps={MenuProps}
            >
              <MenuItem value="">None</MenuItem>

              {optionsForSelect.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case "AVERAGE_SCORE":
        return (
          <>
          </>
        )

      case "PERFORMANCE_ASSESSED_BY":
      case "PROJECT_NAME":
        return (
          <>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <TextField
        select
        label="Type"
        required
        className="text-input"
        fullWidth
        size="small"
        color="secondary"
        value={field.type}
        onChange={handleTypeChange}
        sx={{ mb: 1.5 }}
        error={Boolean(validationErrors?.type)}
        helperText={validationErrors?.type}
        disabled={Boolean(field.id)}
      >
        {formInputType.map((t) => (
          <MenuItem key={t.key} value={t.key}>
            {t.value}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Label"
        className="text-input"
        required
        fullWidth
        size="small"
        color="secondary"
        value={field.label}
        onChange={(e) => setField({ label: e.target.value })}
        onBlur={handleBlur}
        error={isLabelMissing || isDuplicateLabel}
        helperText={getHelperText()}
        sx={{ mb: 1.5 }}
      />
      <TextField
        label="Description"
        className="text-input"
        fullWidth
        size="small"
        color="secondary"
        sx={{ mb: 1.5 }}
        value={field.description || ""}
        onChange={(e) => setField({ description: e.target.value })}
        multiline
        rows={2}
      />

      {OPTION_TYPES.includes(field.type) ||
        ["PERFORMANCE_ASSESSED_BY", "PROJECT_NAME", "CHECKBOX_OPTIONS", "RADIO_OPTIONS"].includes(field.type) ? (
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3, alignItems: "center" }}>
            {!["PERFORMANCE_ASSESSED_BY", "PROJECT_NAME"].includes(field.type) && (
              <>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Options</Typography>
                <IconButtons
                  tooltip={'Add Option'}
                  size="small"
                  color="error"
                  image={active_plus_icon}
                  handleClick={addOption}
                  width={font_13}
                  height={font_13}
                />
              </>

            )}
          </Box>
          {renderOptions()}
        </Box>
      ) : null}

      {renderTypeSpecificControls()}

      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 600,
          mb: 1,
          color: field.type === "AVERAGE_SCORE" ? "error.main" : "inherit",
        }}
      >
        Dependency {field.type === "AVERAGE_SCORE" ? "(required for Average Score)" : "(optional)"}
      </Typography>
      {isAverageScore ? (<AutoCompleteTagInput
        label="Dependent On"
        placeholder={field.dependentOnIds?.length ? null : "Select dependencies"}
        options={(() => {
          const list = getDependencyFieldOptions();
          if (list.length === 0) return [{ label: "No options", all: false }];

          return [
            { label: "Select All", all: true },
            ...list,
          ];
        })()}

        selectedValue={getDependencyFieldOptions().filter((o) =>
          field.dependentOnIds?.includes(o.value)
        )}

        getOptionLabel={(opt) => opt.label ?? ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}

        onChange={(event, newValue) => {

          if (newValue.find((o) => o.all)) {
            const allIds = getDependencyFieldOptions().map((item) => item.value);

            return handleSelectDependencyField(
              field.dependentOnIds?.length === allIds.length ? [] : allIds
            );
          }


          const ids = newValue.map((v) => v.value);
          handleSelectDependencyField(ids);
        }}

        renderOption={(props, option) => {

          if (option.label === "No options") {
            return (
              <Box className="auto-complete-no-options">
                <Box>{option.label}</Box>
              </Box>
            );
          }

          const allOptions = getDependencyFieldOptions();

          const optionChecked =
            (option.all &&
              field.dependentOnIds?.length === allOptions.length) ||
            field.dependentOnIds?.includes(option.value);

          return (
            <li
              {...props}
              key={props.id}
              className={
                optionChecked
                  ? "multi-tag-edit-list-selected"
                  : "multi-tag-style"
              }
            >
              <Checkbox
                className="auto-complete-checkbox-list"
                size="small"
                checked={optionChecked}
                color="secondary"
              />

              {option.label}
            </li>
          );
        }}

        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.label}
              size="small"
            />
          ))
        }
      />) : (<FormControl fullWidth size="small" variant="outlined" className="select-input">
        <InputLabel id="single-dependency-label">Dependent On</InputLabel>
        <Select
          className="text-input select-input"
          color="secondary"
          fullWidth
          size="small"
          margin="dense"
          sx={{ mb: "5px" }}
          labelId="single-dependency-label"
          value={field.dependentOnIds?.[0] ?? []}
          label="Dependent On"
          onChange={(e) => handleSelectDependencyField([e.target.value])}
        >
          <MenuItem value="">None</MenuItem>
          {getDependencyFieldOptions().map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>)}


      {!isAvgScore && <TextField
        label="Dependent Value"
        className="text-input"
        fullWidth
        size="small"
        margin="dense"
        color="secondary"
        type="number"
        value={field.dependentValue ?? ""}
        onChange={(e) => handleSelectDependencyValue(e.target.value)}
        disabled={!hasDependency || field.type?.toUpperCase() === "AVERAGE_SCORE"}
        required={hasDependency && !isAvgScore}
        error={hasDependency && !isAvgScore && !field.dependentValue}
        inputProps={{ min: 0 }}
      />}


      {!isAvgScore && <FormControl
        fullWidth
        size="small"
        sx={{ mt: 1 }}
        variant="outlined"
        className="select-input"
        disabled={!hasDependency || field.type?.toUpperCase() === "AVERAGE_SCORE"}
        required={hasDependency && !isAvgScore}
        error={hasDependency && !isAvgScore && !field.dependentCondition}
      >
        <InputLabel id="dependency-condition-label">Condition</InputLabel>
        <Select
          fullWidth
          size="small"
          margin="dense"
          labelId="dependency-condition-label"
          className="text-input select-input"
          color="secondary"
          value={field.dependentCondition || ""}
          label="Condition"
          onChange={(e) => handleSelectDependencyCondition(e.target.value)}
        >
          <MenuItem value="">None</MenuItem>
          {DEPENDENT_CONDITIONS?.map((c) => (
            <MenuItem key={c.key} value={c.key}>{c.value}</MenuItem>
          ))}
        </Select>
      </FormControl>}

      <Divider sx={{ my: 1 }} />

      <FormControlLabel
        sx={{ mt: 0, ml: 0 }}
        control={
          <Checkbox
            className="auto-complete-checkbox-list"
            size="small"
            color="secondary"
            checked={hasDependency ? false : Boolean(field.required)}
            disabled={hasDependency}
            onChange={(e) => setField({ required: e.target.checked })}
          />
        }
        label={<Typography sx={{ fontSize: "0.85rem", color: hasDependency ? "rgba(0, 0, 0, 0.38)" : "inherit", }}>is this field required?</Typography>}
      />
    </Paper>
  );
};

export default FieldEditor;

